import { useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedCurrency from './FormattedCurrency';
import { Flex } from 'foundations-components/components/Flex';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Well, WellBigNumber, WellHelpText, WellItem, WellLabel } from 'foundations-components/transitional/components/Well';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIRibbon from 'UIComponents/ribbon/UIRibbon';
import PercentChange from './PercentChange';
import ChangePopover from './ChangePopover';
import { Text } from 'foundations-components/components/Text';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const PopoverTarget = styled.span.withConfig({
  displayName: "DataWells__PopoverTarget",
  componentId: "sc-1dec8fk-0"
})(["cursor:default;"]);
const HelpText = ({
  change,
  currencyCode,
  currentAmount,
  helpText,
  isPercentChange,
  isPopoverOpen,
  isTooEarlyInPeriod,
  percentChange,
  previousAmount,
  setIsPopoverOpen,
  title,
  wellType
}) => {
  const shouldShowChangeIndicator = typeof currentAmount === 'number' && typeof previousAmount === 'number' && typeof percentChange === 'number' && Math.abs(percentChange) >= 0.05 && change;
  // if helpText prop provided, simply display that
  if (helpText) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: helpText
    });
  }
  // show dash with message if it's too early to show change in period
  else if (isTooEarlyInPeriod) {
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: "bottom",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "dataWells.weeklyChange.noUpdate"
      }),
      children: /*#__PURE__*/_jsx("span", {
        children: "\u2014"
      })
    });
  }
  // show full change indicator with popover if all data is present
  else if (shouldShowChangeIndicator) {
    return /*#__PURE__*/_jsx(ChangePopover, {
      change: change,
      currencyCode: currencyCode,
      currentAmount: currentAmount,
      isOpen: isPopoverOpen,
      isPercentChangeIndicator: isPercentChange,
      percentChange: percentChange,
      previousAmount: previousAmount,
      setIsOpen: setIsPopoverOpen,
      title: title,
      wellType: wellType,
      children: /*#__PURE__*/_jsx(PopoverTarget, {
        children: percentChange && isPercentChange ? /*#__PURE__*/_jsx(PercentChange, {
          value: percentChange
        }) : currencyCode && /*#__PURE__*/_jsxs(_Fragment, {
          children: [currentAmount > previousAmount && '+', /*#__PURE__*/_jsx(FormattedCurrency, {
            currencyCode: currencyCode,
            value: change
          })]
        })
      })
    });
  }
  return null;
};
export const DataWell = props => {
  const {
    currencyCode,
    displayValue,
    title,
    value
  } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  return /*#__PURE__*/_jsx(WellItem, {
    margin: "0",
    padding: "0",
    children: /*#__PURE__*/_jsx(PopoverTarget, {
      onMouseEnter: () => setIsPopoverOpen(true),
      onMouseLeave: () => setIsPopoverOpen(false),
      children: /*#__PURE__*/_jsxs("div", {
        children: [title && /*#__PURE__*/_jsx(WellLabel, {
          children: title
        }), /*#__PURE__*/_jsx(WellBigNumber, {
          "data-test-id": "deal-management-data-well-amount",
          children: currencyCode && (value || value === 0) ? /*#__PURE__*/_jsx(FormattedCurrency, {
            currencyCode: currencyCode,
            value: value
          }) : displayValue || '—'
        }), /*#__PURE__*/_jsx(WellHelpText, {
          "data-test-id": "deal-management-data-well-help-text",
          children: /*#__PURE__*/_jsx(HelpText, Object.assign({
            isPopoverOpen: isPopoverOpen,
            setIsPopoverOpen: setIsPopoverOpen
          }, props))
        })]
      })
    })
  });
};
const DataWells = ({
  addBetaRibbon,
  helpText,
  isLoading,
  title,
  wells,
  use = 'normal'
}) => {
  return /*#__PURE__*/_jsxs(CardWrapper, {
    marginBlock: "0",
    compact: true,
    blockSize: "100%",
    children: [addBetaRibbon && /*#__PURE__*/_jsx(UIRibbon, {
      use: "beta",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "dataWells.betaRibbon"
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: use === 'normal' ? '800' : '400',
      children: isLoading ? /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        minBlockSize: "81px",
        padding: "0",
        children: /*#__PURE__*/_jsx(UILoadingSpinner, {})
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          marginBlockEnd: "400",
          children: [title && /*#__PURE__*/_jsx(Text, {
            as: "h5",
            typography: "heading-100",
            children: title
          }), helpText && /*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            children: /*#__PURE__*/_jsx(HelpIcon, {
              "data-vr-hidden": "true",
              marginInlineStart: "200",
              title: helpText,
              tooltipPlacement: "top"
            })
          })]
        }), /*#__PURE__*/_jsx(Well, {
          display: "grid",
          margin: "0",
          padding: "0",
          rowGap: "400",
          children: wells.map(well => /*#__PURE__*/_createElement(DataWell, Object.assign({}, well, {
            key: well.key,
            wellType: well.key
          })))
        })]
      })
    })]
  });
};
export default DataWells;